import { Chess } from "@vendor/chess.js";
import type { ChessMove } from "../chess/chessTypes.ts";
import { positionKey } from "../chess/positionKey.ts";
import { engineLinesFor } from "./engineLines.ts";
import { REPERTOIRE_GAP_CP, lossFromLines, topEngineChoices } from "./medals.ts";
import { threatObservation } from "./positionCoaching.ts";

/** A try the opponent did not play, named only when club players actually meet it. */
export type ReplyTurnedDown = {
  san: string;
  name: string;
  why: string;
};

/**
 * A briefing on the move the opponent just played. Written for the person
 * who has to answer it. `prepared` is false when the course has no lesson
 * and the card is only a reading of the board.
 */
export type ReplyBriefing = {
  san: string;
  name: string | null;
  prepared: boolean;
  idea: string;
  onTheBoard: string;
  theyWant?: string;
  theyHope?: string;
  cost?: string;
  turnedDown?: ReplyTurnedDown[];
  watch?: string;
  /** The opening the moves have reached, when the title does not already say it. */
  opening?: string;
  /** Stockfish's view, only when the move is a sideline or worse. In words, no centipawns. */
  soundness?: string;
};

export type ReplyScript = {
  /** Moves from the start position, including the reply itself. */
  line: string;
  briefing: Omit<ReplyBriefing, "san" | "prepared">;
};

const byPosition = new Map<string, ReplyBriefing>();

function bare(san: string): string {
  return san.replace(/[+#?!]/g, "");
}

function keyFor(fenBefore: string, san: string): string {
  return `${positionKey(fenBefore)} ${bare(san)}`;
}

/** Register briefings. The same position keeps the first briefing written for it. */
export function registerReplies(scripts: ReplyScript[]): void {
  for (const script of scripts) {
    const sans = script.line.trim().split(/\s+/).filter(Boolean);
    const san = sans.pop();
    if (!san) continue;
    const board = new Chess();
    let legal = true;
    for (const move of sans) {
      if (!board.move(move)) {
        legal = false;
        break;
      }
    }
    const played = legal ? board.move(san) : null;
    if (!played) {
      throw new Error(`Reply briefing is not a legal line: ${script.line}`);
    }
    board.undo();
    const key = keyFor(board.fen(), san);
    if (byPosition.has(key)) continue;
    byPosition.set(key, { ...script.briefing, san: played.san, prepared: true });
  }
}

export function preparedReply(fenBefore: string, san: string): ReplyBriefing | null {
  return byPosition.get(keyFor(fenBefore, san)) ?? null;
}

const NAMES: Record<string, string> = {
  p: "pawn",
  n: "knight",
  b: "bishop",
  r: "rook",
  q: "queen",
  k: "king",
};

const PIECE_RANK: Record<string, number> = { k: 6, q: 5, r: 4, b: 3, n: 2, p: 1 };

const KNIGHT_DELTAS: Array<[number, number]> = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];
const KING_DELTAS: Array<[number, number]> = [
  [1, 0],
  [1, 1],
  [0, 1],
  [-1, 1],
  [-1, 0],
  [-1, -1],
  [0, -1],
  [1, -1],
];
const BISHOP_DELTAS: Array<[number, number]> = [
  [1, 1],
  [1, -1],
  [-1, 1],
  [-1, -1],
];
const ROOK_DELTAS: Array<[number, number]> = [
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
];

type BoardPiece = { type: string; color: string };

/** A course-graph edge for this move. The card may quote it, and it is still a reading. */
export type GraphReplyNote = {
  name: string | null;
  note: string;
  warned: boolean;
};

type CourseEdge = {
  san: string;
  nextPositionKey: string;
  role: string;
  explanation: string;
  variationName?: string;
  tacticalWarning?: boolean;
};

type CourseGraph = {
  nodes: Record<
    string,
    {
      moves: CourseEdge[];
      variationName?: string;
    }
  >;
};

function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts[0] || "";
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

function pieceMap(chess: Chess): Map<string, BoardPiece> {
  const map = new Map<string, BoardPiece>();
  chess.board().forEach((rank, row) => {
    rank.forEach((piece, file) => {
      if (!piece) return;
      map.set(`${"abcdefgh"[file]}${8 - row}`, piece);
    });
  });
  return map;
}

function squareAt(file: number, rank: number): string | null {
  if (file < 0 || file > 7 || rank < 1 || rank > 8) return null;
  return `${"abcdefgh"[file]}${rank}`;
}

/** Squares this piece attacks, including friendly pieces it defends. Blockers stop a slider. */
function attackedSquares(board: Map<string, BoardPiece>, square: string): string[] {
  const piece = board.get(square);
  if (!piece) return [];
  const file = square.charCodeAt(0) - 97;
  const rank = Number(square[1]);
  const hits: string[] = [];
  const add = (nextFile: number, nextRank: number) => {
    const name = squareAt(nextFile, nextRank);
    if (name) hits.push(name);
    return name;
  };
  if (piece.type === "p") {
    const dir = piece.color === "w" ? 1 : -1;
    add(file - 1, rank + dir);
    add(file + 1, rank + dir);
    return hits;
  }
  const deltas =
    piece.type === "n"
      ? KNIGHT_DELTAS
      : piece.type === "k"
        ? KING_DELTAS
        : piece.type === "b"
          ? BISHOP_DELTAS
          : piece.type === "r"
            ? ROOK_DELTAS
            : piece.type === "q"
              ? [...BISHOP_DELTAS, ...ROOK_DELTAS]
              : [];
  const slider = piece.type === "b" || piece.type === "r" || piece.type === "q";
  for (const [df, dr] of deltas) {
    let nextFile = file + df;
    let nextRank = rank + dr;
    while (squareAt(nextFile, nextRank)) {
      const name = squareAt(nextFile, nextRank)!;
      hits.push(name);
      if (!slider || board.has(name)) break;
      nextFile += df;
      nextRank += dr;
    }
  }
  return hits;
}

function notableSquares(
  squares: string[],
  board: Map<string, BoardPiece>,
  color: string,
  enemy: boolean
): string[] {
  return squares
    .filter((square) => {
      const piece = board.get(square);
      if (!piece) return false;
      if (enemy && piece.type === "k") return false;
      return enemy ? piece.color !== color : piece.color === color;
    })
    .sort((a, b) => PIECE_RANK[board.get(b)!.type] - PIECE_RANK[board.get(a)!.type])
    .slice(0, 2);
}

function piecePhrase(
  squares: string[],
  board: Map<string, BoardPiece>,
  owner: "your" | "their"
): string {
  return joinAnd(
    squares.map((square) => {
      const piece = board.get(square)!;
      return `${owner} ${NAMES[piece.type] || "piece"} on ${square}`;
    })
  );
}

/** What the moved piece now attacks, and what it stopped defending. No claim that a piece is loose. */
function pieceJob(
  before: Map<string, BoardPiece>,
  after: Map<string, BoardPiece>,
  from: string,
  to: string,
  color: string,
  subject = "It"
): string {
  const beforeHits = new Set(attackedSquares(before, from));
  const afterSet = new Set(attackedSquares(after, to));
  const gained = [...afterSet].filter((square) => !beforeHits.has(square));
  const lost = [...beforeHits].filter((square) => !afterSet.has(square));
  const attacks = notableSquares(gained, after, color, true);
  // A defender that walked away matters only when the piece it left is
  // under attack; otherwise it is trivia.
  const reader = color === "w" ? "b" : "w";
  const abandoned = notableSquares(lost, before, color, false).filter(
    (square) => after.has(square) && attackersOf(after, square, reader).length > 0
  );
  const sentences: string[] = [];
  if (attacks.length)
    sentences.push(`${subject} now attacks ${piecePhrase(attacks, after, "your")}.`);
  if (abandoned.length)
    sentences.push(`${subject} no longer defends ${piecePhrase(abandoned, before, "their")}.`);
  return sentences.join(" ");
}

/** The course's own note on a move it names, with its warning first. */
function graphIdea(graph: GraphReplyNote): string {
  const note = graph.note.trim();
  const named =
    graph.name && !note.toLowerCase().includes(graph.name.toLowerCase())
      ? `The course names this the ${graph.name}. `
      : "";
  const warning = graph.warned ? "The course warns against this move. " : "";
  return `${warning}${named}${note}`.replace(/\s+/g, " ").trim();
}

/** Pieces of `color` that attack the square. Sliders stop at the first piece. */
function attackersOf(board: Map<string, BoardPiece>, square: string, color: string): string[] {
  const hits: string[] = [];
  for (const [from, piece] of board) {
    if (piece.color !== color) continue;
    if (attackedSquares(board, from).includes(square)) hits.push(from);
  }
  return hits;
}

/**
 * The course already named this move. Null when the position is off the graph.
 * A prepared briefing still wins over this note.
 */
export function graphReplyNote(
  course: CourseGraph | null | undefined,
  fenBefore: string,
  san: string
): GraphReplyNote | null {
  const node = course?.nodes[positionKey(fenBefore)];
  if (!node) return null;
  const edge = node.moves.find((move) => bare(move.san) === bare(san));
  if (!edge) return null;
  const arrived = course?.nodes[edge.nextPositionKey];
  const name = (edge.variationName || arrived?.variationName || "").trim() || null;
  const note = edge.explanation.trim();
  if (!name && !note) return null;
  return {
    name,
    note,
    warned: edge.role === "mistake" || edge.tacticalWarning === true,
  };
}

function moveHeadline(move: ChessMove, mover: "w" | "b"): string {
  const name = mover === "w" ? "White" : "Black";
  if (/^O-O-O/.test(move.san)) {
    return `${name} castles queenside. The king leaves the center and a rook arrives on the d-file.`;
  }
  if (/^O-O/.test(move.san)) {
    return `${name} castles kingside. The king leaves the center and a rook arrives on the f-file.`;
  }
  if (move.promotion) {
    return `${name} promotes the pawn to a ${NAMES[move.promotion] || "piece"} on ${move.to}.`;
  }
  if (move.captured)
    return `${name} captures your ${NAMES[move.captured] || "piece"} on ${move.to}.`;
  if (move.piece === "p") return `${name} moves the pawn from ${move.from} to ${move.to}.`;
  return `${name} moves the ${NAMES[move.piece || "n"] || "piece"} from ${move.from} to ${move.to}.`;
}

function rookShift(san: string, mover: "w" | "b"): { from: string; to: string } | null {
  if (/^O-O-O/.test(san))
    return mover === "w" ? { from: "a1", to: "d1" } : { from: "a8", to: "d8" };
  if (/^O-O/.test(san)) return mover === "w" ? { from: "h1", to: "f1" } : { from: "h8", to: "f8" };
  return null;
}

/**
 * A reading of an unauthored move: what the piece did, what it now attacks,
 * and the threat to answer. It does not invent an opening or a plan. When the
 * course graph names the move, its note leads.
 */
export function fallbackReply(
  fenBefore: string,
  move: ChessMove,
  mover: "w" | "b",
  graph?: GraphReplyNote | null
): ReplyBriefing {
  const beforeBoard = new Chess(fenBefore);
  const before = pieceMap(beforeBoard);
  const played = beforeBoard.move({
    from: move.from,
    to: move.to,
    ...(move.promotion ? { promotion: move.promotion } : {}),
  });
  const after = pieceMap(beforeBoard);
  const rook = rookShift(move.san, mover);
  const jobs = [
    pieceJob(before, after, move.from, move.to, mover),
    rook ? pieceJob(before, after, rook.from, rook.to, mover, "The rook") : "",
  ].filter(Boolean);
  const check = beforeBoard.isCheckmate()
    ? "You are in checkmate."
    : beforeBoard.inCheck()
      ? "You are in check."
      : "";
  const headline = moveHeadline(played || move, mover);
  const threat = threatObservation(beforeBoard, mover === "w" ? "b" : "w");
  const board = [check, ...jobs].filter(Boolean).join(" ");
  return {
    san: move.san,
    name: graph?.name ?? null,
    prepared: false,
    idea: graph ? graphIdea(graph) : headline,
    onTheBoard: graph ? [headline, board].filter(Boolean).join(" ") : board,
    watch: threat,
  };
}

/** A sideline is this far behind the best line; a mistake, this far. */
export const SIDELINE_CP = REPERTOIRE_GAP_CP;
export const MISTAKE_CP = 150;

/**
 * Stockfish's view of the reply, in words, from a search that already scored
 * the position. Empty when the move is one of the first three choices, when
 * it is close enough to count as a repertoire choice, or when no search has
 * been stored: a book move needs no second opinion.
 */
export function replySoundness(fenBefore: string, san: string): string {
  const lines = engineLinesFor(fenBefore);
  if (!lines || lines.length < 2) return "";
  let played: { uci: string; color: "w" | "b" };
  try {
    const moved = new Chess(fenBefore).move(san);
    if (!moved) return "";
    played = {
      uci: `${moved.from}${moved.to}${moved.promotion || ""}`,
      color: moved.color === "b" ? "b" : "w",
    };
  } catch {
    return "";
  }
  const choices = topEngineChoices(fenBefore, lines, 3);
  const best = choices[0];
  if (!best || choices.some((choice) => choice.uci === played.uci)) return "";
  const loss = lossFromLines(lines, played.uci);
  if (loss == null || loss <= SIDELINE_CP) return "";
  const dots = played.color === "b" ? "…" : "";
  const preferred = `${dots}${bare(best.san)}`;
  const move = `${dots}${bare(san)}`;
  if (best.mate != null && best.mate > 0) {
    return `${move} walks into trouble. Stockfish sees a forced win after ${preferred}.`;
  }
  return loss >= MISTAKE_CP
    ? `${move} gives up ground. Stockfish prefers ${preferred}.`
    : `${move} is a sideline. Stockfish prefers ${preferred}.`;
}

const SQUARE = /\b([a-h][1-8])\b/g;

/** The squares the card's always-visible lines name, in reading order. */
export function replySquares(briefing: ReplyBriefing, limit = 6): string[] {
  const seen = new Set<string>();
  for (const text of [briefing.idea, briefing.theyHope, briefing.watch]) {
    for (const match of (text || "").matchAll(SQUARE)) {
      seen.add(match[1]!);
      if (seen.size >= limit) return [...seen];
    }
  }
  return [...seen];
}

function mentions(text: string, san: string): boolean {
  const escaped = san.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  return new RegExp(`(^|[^A-Za-z0-9])…?${escaped}(?![A-Za-z0-9-])`).test(text);
}

function withoutSentences(text: string | undefined, san: string): string {
  if (!text) return "";
  return text
    .split(/(?<=[.!?])\s+/)
    .filter((sentence) => !mentions(sentence, san))
    .join(" ")
    .trim();
}

/**
 * The briefing with every sentence that names the student's course move
 * removed, for the modes that ask the student to recall it. The move is
 * matched as a whole word, with or without the Black ellipsis.
 */
export function withheldReply(
  briefing: ReplyBriefing,
  san: string | null | undefined
): ReplyBriefing {
  const move = san ? bare(san) : "";
  if (!move) return briefing;
  const idea = withoutSentences(briefing.idea, move);
  const watch = withoutSentences(briefing.watch, move);
  const trimmed = (text: string | undefined) => {
    const kept = withoutSentences(text, move);
    return kept ? { kept } : null;
  };
  const theyWant = trimmed(briefing.theyWant);
  const theyHope = trimmed(briefing.theyHope);
  const cost = trimmed(briefing.cost);
  const onTheBoard = withoutSentences(briefing.onTheBoard, move);
  // When the idea itself named the move, the board reading takes its place
  // in the first tier and is not repeated in the second.
  const promoted = !idea && Boolean(onTheBoard);
  return {
    ...briefing,
    idea:
      idea ||
      onTheBoard ||
      "The lesson's note names your answer, so it waits until you have played.",
    onTheBoard: promoted ? "" : onTheBoard,
    watch: watch || "Check checks, captures, and threats before you answer.",
    ...(theyWant ? { theyWant: theyWant.kept } : { theyWant: undefined }),
    ...(theyHope ? { theyHope: theyHope.kept } : { theyHope: undefined }),
    ...(cost ? { cost: cost.kept } : { cost: undefined }),
    turnedDown: briefing.turnedDown?.filter((alt) => !mentions(alt.why, move)),
  };
}

export function replyForMove(
  fenBefore: string,
  move: ChessMove,
  mover: "w" | "b",
  graph?: GraphReplyNote | null
): ReplyBriefing {
  return preparedReply(fenBefore, move.san) ?? fallbackReply(fenBefore, move, mover, graph);
}

export function replyTitle(briefing: ReplyBriefing, moveNumber: number, mover: "w" | "b"): string {
  const move = mover === "b" ? `...${briefing.san}` : `${moveNumber}. ${briefing.san}`;
  return briefing.name ? `${move} · ${briefing.name}` : move;
}
